import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { ShareButton } from "@/components/ShareButton";
import { getQuoteContext, relatedQuoteContexts, QUOTE_CONTEXTS } from "@/data/quote-contexts";
import { getStory } from "@/data/stories";
import { getGlossaryEntry } from "@/data/glossary";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/quotes/$slug")({
  loader: ({ params }) => {
    const entry = getQuoteContext(params.slug);
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ loaderData }) => {
    const e = loaderData?.entry;
    if (!e) {
      return {
        meta: [
          { title: "Quote not found — Mr. B Quotes" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `${SITE}/quotes/${e.slug}`;
    const desc = e.dek.length > 155 ? `${e.dek.slice(0, 152)}...` : e.dek;
    const bare = e.quote.replace(/[.!?]+$/, "");
    const short = `${bare} — Meaning & Origin`;
    return {
      meta: [
        { title: e.title.length > 60 ? (short.length > 60 ? `${bare} — Explained` : short) : e.title },
        { name: "description", content: desc },
        { property: "og:title", content: e.title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: e.title },
        { name: "twitter:description", content: desc },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: e.title,
            description: e.dek,
            mainEntityOfPage: url,
            about: { "@type": "Quotation", text: e.quote, spokenByCharacter: e.actuallyFrom },
            author: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
            publisher: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: e.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Quotes Explained", item: `${SITE}/quotes` },
              { "@type": "ListItem", position: 3, name: e.quote, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Explained" active="quotes" />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-5xl text-gold">No record of that one</h1>
        <p className="mt-4 text-muted-foreground">We haven&rsquo;t written an explainer for that quote yet.</p>
        <Link to="/quotes" className="mt-8 inline-block rounded-full border border-border px-5 py-2 text-sm hover:border-gold">
          Browse all {QUOTE_CONTEXTS.length} explainers →
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  component: QuoteContextPage,
});

function Section({ label, heading, paras }: { label: string; heading: string; paras: string[] }) {
  return (
    <section className="mt-12">
      <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{label}</div>
      <h2 className="mt-2 font-display text-3xl leading-snug">{heading}</h2>
      <div className="mt-5 space-y-5 text-base sm:text-lg text-foreground/90 leading-relaxed">
        {paras.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </section>
  );
}

function QuoteContextPage() {
  const { entry: e } = Route.useLoaderData();
  const related = relatedQuoteContexts(e.slug, 3);
  const stories = e.stories.map(getStory).filter((s): s is NonNullable<typeof s> => Boolean(s));
  const terms = e.glossary.map(getGlossaryEntry).filter((t): t is NonNullable<typeof t> => Boolean(t));

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Explained" active="quotes" />

      <main>
        <div className="border-b border-border/60">
          <div className="mx-auto max-w-3xl px-4 pt-6">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-gold">Home</Link>
              <span className="mx-2 opacity-50">/</span>
              <Link to="/quotes" className="hover:text-gold">Quotes Explained</Link>
              <span className="mx-2 opacity-50">/</span>
              <span className="text-foreground line-clamp-1">{e.quote}</span>
            </nav>
          </div>
          <div className="mx-auto max-w-3xl px-4 py-10 sm:py-14">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{e.verdict}</div>
            <blockquote className="mt-4 font-display text-3xl sm:text-5xl leading-tight border-l-2 border-gold pl-5">
              &ldquo;{e.quote}&rdquo;
            </blockquote>
            <h1 className="mt-6 text-xl sm:text-2xl font-medium">{e.title}</h1>
            <p className="mt-4 text-lg text-muted-foreground leading-relaxed">{e.dek}</p>

            <dl className="mt-8 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-card/40 p-4">
                <dt className="text-[10px] uppercase tracking-widest text-muted-foreground">Usually credited to</dt>
                <dd className="mt-1 text-base">{e.popularlyCreditedTo}</dd>
              </div>
              <div className="rounded-xl border border-gold/40 bg-gold/5 p-4">
                <dt className="text-[10px] uppercase tracking-widest text-gold">The evidence points to</dt>
                <dd className="mt-1 text-base">{e.actuallyFrom}</dd>
              </div>
            </dl>
          </div>
        </div>

        <article className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
          <div className="rounded-2xl border border-gold/30 bg-gold/5 p-6 sm:p-8">
            <div className="text-[10px] uppercase tracking-widest text-gold">Short answer</div>
            <p className="mt-3 text-lg leading-relaxed">{e.shortAnswer}</p>
          </div>

          <Section label="Origin" heading="Where the line actually comes from" paras={e.origin} />
          <Section label="Meaning" heading="What it originally meant" paras={e.meaning} />
          <Section label="Misuse" heading="How it gets used today — and why that&rsquo;s off" paras={e.misuse} />
          <Section label="Use it well" heading="An honest way to quote it" paras={e.useWell} />

          <section className="mt-14 border-t border-border/60 pt-10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Questions</div>
            <h2 className="mt-2 font-display text-3xl">People also ask</h2>
            <div className="mt-6 space-y-5">
              {e.faqs.map((f, i) => (
                <div key={i} className="rounded-xl border border-border bg-card/40 p-5">
                  <h3 className="font-medium text-lg">{f.q}</h3>
                  <p className="mt-2 text-muted-foreground leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {stories.length > 0 && (
            <section className="mt-14 border-t border-border/60 pt-10">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Read next</div>
              <h2 className="mt-2 font-display text-2xl">Stories on the same idea</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {stories.map((s) => (
                  <Link
                    key={s.slug}
                    to="/stories/$slug"
                    params={{ slug: s.slug }}
                    className="group rounded-xl border border-border bg-card/40 p-4 transition hover:border-gold"
                  >
                    <div className="font-display text-lg group-hover:text-gold transition">{s.title}</div>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{s.dek}</p>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {terms.length > 0 && (
            <section className="mt-12">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Name the technique</div>
              <div className="mt-4 flex flex-wrap gap-2">
                {terms.map((t) => (
                  <Link
                    key={t.slug}
                    to="/glossary/$slug"
                    params={{ slug: t.slug }}
                    className="rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground hover:border-gold hover:text-gold transition"
                  >
                    {t.term}
                  </Link>
                ))}
              </div>
            </section>
          )}

          {related.length > 0 && (
            <section className="mt-14 border-t border-border/60 pt-10">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">See also</div>
              <h2 className="mt-2 font-display text-2xl">More quotes worth checking</h2>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to="/quotes/$slug"
                    params={{ slug: r.slug }}
                    className="group rounded-xl border border-border bg-card/40 p-4 transition hover:border-gold"
                  >
                    <div className="text-[10px] uppercase tracking-widest text-gold">{r.verdict}</div>
                    <div className="mt-1 font-display text-lg group-hover:text-gold transition">&ldquo;{r.quote}&rdquo;</div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          <div className="mt-12 flex flex-wrap items-center gap-3">
            <ShareButton
              text={`"${e.quote}"\n\n${e.shortAnswer}\n\nRead the full story at ${SITE}/quotes/${e.slug}`}
              title={e.title}
              label="Share this explainer"
            />
            <Link to="/quotes" className="rounded-full border border-border px-5 py-2.5 text-sm hover:border-gold transition">
              ← All quote explainers
            </Link>
            <Link to="/games" className="rounded-full bg-gold/10 border border-gold/40 px-5 py-2.5 text-sm text-gold hover:bg-gold/15 transition">
              Play the quote games →
            </Link>
          </div>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
}
